import { ReactNode, useMemo } from "react";

import InfiniteScroll from "react-infinite-scroll-component";

import { Grid, Skeleton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  loadMoreList,
  setupControlPage,
} from "./redux/controlInfinitiveScrolling";
import { controlInfinitiveScrollSelector } from "./redux/selector";
import { InfinitiveScrollWrapper } from "./styles";
import { InfiniteScrollKey, LIMIT } from "./const";
import { useLegacyEffect } from "helper/customHook";
import { isNumber } from "lodash";

interface ItemColumns {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  xxl?: number;
}

interface Props {
  id?: string;
  data: any;
  fetchMoreData?: VoidFunction;
  children: (props: any) => ReactNode;
  defaultLimit?: number;
  itemColumns?: ItemColumns | number;
  hasMore?: boolean;
  spacing?: number;
  maxHeight?: string | number;
  style?: object;
  wrapperStyle?: object;
}

const itemColumnsRender = (itemColumns: ItemColumns | number): ItemColumns => {
  if (isNumber(itemColumns)) {
    return { xs: itemColumns };
  }
  return itemColumns;
};

const ListInfinitiveScrolling = ({
  id,
  data,
  fetchMoreData,
  children,
  defaultLimit,
  itemColumns = 12,
  hasMore,
  spacing = 3,
  maxHeight = "calc(100vh - 220px)",
  style = {},
  wrapperStyle = {},
}: Props) => {
  const dispatch = useDispatch();

  const { offset, limit } = useSelector(
    controlInfinitiveScrollSelector(id ?? InfiniteScrollKey.DEFAULT)
  );

  useLegacyEffect(() => {
    if (id && id !== InfiniteScrollKey.DEFAULT) {
      dispatch(setupControlPage({ id, limit: defaultLimit ?? LIMIT }));
    }
  }, []);

  const loadMoreData = () => {
    dispatch(loadMoreList({ id }));
    fetchMoreData?.();
  };

  const hasMoreData = useMemo(() => {
    return hasMore ?? data?.length === offset + limit;
  }, [hasMore, data?.length, offset, limit]);

  return (
    <InfinitiveScrollWrapper style={wrapperStyle}>
      <InfiniteScroll
        dataLength={data?.length || 0}
        next={loadMoreData}
        hasMore={hasMoreData}
        loader={<Skeleton variant="text" width={"100%"} />}
        height={maxHeight}
        style={style}
      >
        <Grid container spacing={spacing}>
          {(data ?? []).map((item: any, index: number) => (
            <Grid key={index} item {...itemColumnsRender(itemColumns)}>
              {children({ data: item, index })}
            </Grid>
          ))}
        </Grid>
      </InfiniteScroll>
    </InfinitiveScrollWrapper>
  );
};

export default ListInfinitiveScrolling;
